import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, Loader2, Plus, X, Sparkles, TrendingDown, AlertTriangle, Lightbulb } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Portfolio context for RAG
export interface PortfolioContext {
  totalValue: number;
  expectedReturn: number;
  volatility: number;
  sharpeRatio: number;
  totalFees: number;
  holdings: Array<{
    ticker: string;
    name?: string;
    shares: number;
    value: number;
    weight: number;
    costBasis?: number;
  }>;
  // Model tab comparison data (optional)
  modelComparison?: {
    timeRange: string;
    viewMode: string;
    yourPortfolio: { value: number; totalReturn: number };
    sp500: { value: number; totalReturn: number };
    tacticalFund: { name: string; value: number; totalReturn: number };
  };
}

interface Chat {
  id: string;
  user_id: string;
  title: string | null;
  created_at: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
}

interface ProactiveInsight {
  id: string;
  icon: 'warning' | 'opportunity' | 'tip';
  title: string;
  question: string;
}

interface ChatInterfaceProps {
  portfolioContext?: PortfolioContext;
}

// Generate proactive insights based on portfolio data
function generateInsights(context?: PortfolioContext): ProactiveInsight[] {
  if (!context) return [];

  const insights: ProactiveInsight[] = [];

  // Check for concentration risk (any holding > 10%)
  const concentratedHoldings = context.holdings.filter(h => h.weight > 0.10);
  if (concentratedHoldings.length > 0) {
    const top = concentratedHoldings[0];
    insights.push({
      id: 'concentration',
      icon: 'warning',
      title: `${top.ticker} is ${(top.weight * 100).toFixed(0)}% of portfolio`,
      question: `My ${top.ticker} position seems concentrated. What are the risks of having ${(top.weight * 100).toFixed(0)}% in one stock?`,
    });
  }

  // Check for low Sharpe ratio
  if (context.sharpeRatio < 0.5) {
    insights.push({
      id: 'sharpe',
      icon: 'tip',
      title: `Sharpe ratio is ${context.sharpeRatio.toFixed(2)}`,
      question: `My Sharpe ratio is ${context.sharpeRatio.toFixed(2)} which seems low. How can I improve my risk-adjusted returns?`,
    });
  }

  // Check for tax loss harvesting opportunities
  const lossHoldings = context.holdings.filter(h => h.costBasis && h.value < h.costBasis * h.weight);
  if (lossHoldings.length > 0) {
    insights.push({
      id: 'tlh',
      icon: 'opportunity',
      title: 'Tax loss harvesting available',
      question: 'Do I have any tax loss harvesting opportunities in my portfolio?',
    });
  }

  // High fees warning
  const feeRate = context.totalFees / context.totalValue;
  if (feeRate > 0.005) { // > 0.5%
    insights.push({
      id: 'fees',
      icon: 'warning',
      title: `Fees are ${(feeRate * 100).toFixed(2)}%/year`,
      question: `I'm paying ${(feeRate * 100).toFixed(2)}% in annual fees. Is this too high and how can I reduce it?`,
    });
  }

  // Model comparison insight
  if (context.modelComparison) {
    const yourReturn = context.modelComparison.yourPortfolio.totalReturn;
    const spReturn = context.modelComparison.sp500.totalReturn;
    if (spReturn > yourReturn + 0.1) { // S&P outperformed by >10%
      insights.push({
        id: 'underperform',
        icon: 'tip',
        title: 'Portfolio trailing S&P 500',
        question: `The S&P 500 returned ${(spReturn * 100).toFixed(1)}% vs my ${(yourReturn * 100).toFixed(1)}%. Why is my portfolio underperforming?`,
      });
    }
  }

  return insights.slice(0, 3); // Max 3 insights
}

export function ChatInterface({ portfolioContext }: ChatInterfaceProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Generate proactive insights
  const insights = generateInsights(portfolioContext);

  // Auto-focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Fetch messages for active chat
  const { data: messages = [] } = useQuery({
    queryKey: ['messages', activeChatId],
    queryFn: async () => {
      if (!activeChatId) return [];
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('chat_id', activeChatId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as Message[];
    },
    enabled: !!activeChatId,
  });

  // Create new chat
  const createChat = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Must be logged in to start a chat');

    const { data, error } = await supabase
      .from('chats')
      .insert({ title: 'New Conversation', user_id: user.id })
      .select()
      .single();
    if (error) throw error;

    setActiveChatId(data.id);
    queryClient.invalidateQueries({ queryKey: ['chats'] });
    return data as Chat;
  }, [queryClient]);

  // Send message with streaming
  const sendMessage = useCallback(async (content: string) => {
    let chatId = activeChatId;
    if (!chatId) {
      const newChat = await createChat();
      chatId = newChat.id;
    }

    // Save user message
    await supabase
      .from('messages')
      .insert({ chat_id: chatId, role: 'user', content });

    // Refresh messages to show user message immediately
    queryClient.invalidateQueries({ queryKey: ['messages', chatId] });

    // Build conversation history for multi-turn context
    const conversationHistory = messages
      .filter(m => m.role !== 'system')
      .map(m => ({ role: m.role, content: m.content }));
    conversationHistory.push({ role: 'user', content });

    // Start streaming
    setIsStreaming(true);
    setStreamingContent('');

    try {
      // Get auth token for the edge function
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-stream`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            chatId,
            messages: conversationHistory,
            portfolioContext,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Read the stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') continue;

              try {
                const parsed = JSON.parse(data);
                if (parsed.error) {
                  console.error('[Stream Error]', parsed.error);
                  toast.error(parsed.error);
                  return;
                }
                if (parsed.content) {
                  fullContent += parsed.content;
                  setStreamingContent(fullContent);
                }
              } catch {
                // Ignore parse errors for incomplete chunks
              }
            }
          }
        }
      }

      // Save assistant message to DB
      if (fullContent) {
        await supabase
          .from('messages')
          .insert({ chat_id: chatId, role: 'assistant', content: fullContent });
      }

      queryClient.invalidateQueries({ queryKey: ['messages', chatId] });
    } catch (error) {
      console.error('[Chat Error]', error);
      toast.error('Failed to get response. Please try again.');
    } finally {
      setIsStreaming(false);
      setStreamingContent('');
    }
  }, [activeChatId, createChat, messages, portfolioContext, queryClient]);

  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isStreaming) return;
    const message = inputValue;
    setInputValue('');
    sendMessage(message);
  };

  // Handle insight click
  const handleInsightClick = (insight: ProactiveInsight) => {
    sendMessage(insight.question);
  };

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamingContent]);

  const getInsightIcon = (type: ProactiveInsight['icon']) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="h-3 w-3" />;
      case 'opportunity': return <TrendingDown className="h-3 w-3" />;
      case 'tip': return <Lightbulb className="h-3 w-3" />;
    }
  };

  if (!isOpen) {
    return (
      <Button
        className="fixed bottom-4 right-4 h-12 w-12 rounded-full shadow-lg z-50"
        onClick={() => setIsOpen(true)}
      >
        <MessageSquare className="h-6 w-6" />
      </Button>
    );
  }

  return (
    <Card className="fixed bottom-4 right-4 w-[400px] h-[600px] shadow-xl z-50 flex flex-col">
      <CardHeader className="p-4 border-b flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          AI Advisor
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setActiveChatId(null);
              createChat();
            }}
            title="New Chat"
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        <ScrollArea className="flex-1 p-4">
          {messages.length === 0 && !streamingContent ? (
            <div className="space-y-4">
              <div className="text-center text-muted-foreground text-sm">
                <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Ask me anything about your portfolio</p>
              </div>

              {/* Proactive Insights */}
              {insights.length > 0 && (
                <div className="space-y-2 mt-4">
                  <p className="text-xs text-muted-foreground font-medium">Suggested topics:</p>
                  {insights.map((insight) => (
                    <button
                      key={insight.id}
                      onClick={() => handleInsightClick(insight)}
                      className="w-full text-left p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-xs",
                            insight.icon === 'warning' && "border-amber-500 text-amber-500",
                            insight.icon === 'opportunity' && "border-green-500 text-green-500",
                            insight.icon === 'tip' && "border-blue-500 text-blue-500"
                          )}
                        >
                          {getInsightIcon(insight.icon)}
                        </Badge>
                        <span className="text-sm font-medium">{insight.title}</span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {insight.question}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={cn(
                    "max-w-[85%] rounded-lg px-3 py-2 text-sm break-words whitespace-pre-wrap",
                    msg.role === 'user'
                      ? "ml-auto bg-primary text-primary-foreground"
                      : "bg-muted"
                  )}
                >
                  {msg.content}
                </div>
              ))}
              {/* Streaming content */}
              {streamingContent && (
                <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm break-words whitespace-pre-wrap bg-muted">
                  {streamingContent}
                  <span className="inline-block w-2 h-4 bg-primary/50 animate-pulse ml-1" />
                </div>
              )}
              {/* Loading indicator (before streaming starts) */}
              {isStreaming && !streamingContent && (
                <div className="bg-muted w-max rounded-lg px-3 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
              <div ref={scrollRef} />
            </div>
          )}
        </ScrollArea>

        <div className="p-4 border-t mt-auto">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your question..."
              disabled={isStreaming}
            />
            <Button type="submit" size="icon" disabled={isStreaming}>
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
}
